import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAniList } from '../context/AniListContext';
import { useTimeSettings } from '../context/TimeSettingsContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useUndoRedo } from '../context/UndoRedoContext';
import { useLinkSettings } from '../context/LinkSettingsContext';
import { useAlert } from '../context/ToastContext';
import { formatDuration, formatEpisodeProgress, getTotalEpisodes, calculateAnimeTime, getMediaStatusBadge } from '../utils/timeFormatter';
import { formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { updateAnimeStatus, updateAnimeProgress, updateMediaScore } from '../services/aniListService';
import { getSimpleCache } from '../services/cacheService';
import { LOCAL_STORAGE_KEYS, OP_ED_SKIP_MINUTES } from '../config/constants';
import { CardGridSkeleton } from './common/SkeletonLoader';
import StatusButton from './StatusButton';
import EpisodeCounter from './EpisodeCounter';
import SiteLinksModal from './SiteLinksModal';
import ScoreInput from './ScoreInput';
import './QuickWins.css';
import './Dashboard.css';

const SORT_OPTIONS = [
    { id: 'remainingTime', label: '⏳ Remaining Time' },
    { id: 'name', label: '📝 Name' },
    { id: 'averageScore', label: '⭐ Average Score' },
    { id: 'totalAnime', label: '📚 Total Anime' },
    { id: 'totalDuration', label: '⏱️ Total Duration' },
    { id: 'watchedTime', label: '✅ Watched Time' }
];

const GROUPS_PER_PAGE = 50;
const QUICK_WINS_SORT_BY_KEY = 'quick_wins_sort_by';
const QUICK_WINS_SORT_DIR_KEY = 'quick_wins_sort_direction';

const QuickWins = () => {
    const navigate = useNavigate();
    const { animeGroups, isRefreshing, isLoading, userData, refreshData } = useAniList();
    const { hoursPerDay, quickWinHours, setQuickWinHours } = useTimeSettings();
    const { titlePreference } = useTitlePreference();
    const { addChange } = useUndoRedo();
    const { watchLinks } = useLinkSettings();
    const { showError } = useAlert();
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showWatchModal, setShowWatchModal] = useState(false);
    const [selectedAnimeForModal, setSelectedAnimeForModal] = useState('');
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [optimisticProgress, setOptimisticProgress] = useState(new Map());
    const [updatingProgress, setUpdatingProgress] = useState(new Set());
    const [optimisticScores, setOptimisticScores] = useState(new Map());
    const [updatingScores, setUpdatingScores] = useState(new Set());
    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem(QUICK_WINS_SORT_BY_KEY) || 'remainingTime';
    });
    const [sortDirection, setSortDirection] = useState(() => {
        return localStorage.getItem(QUICK_WINS_SORT_DIR_KEY) || 'asc';
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));

    // Save sort options to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(QUICK_WINS_SORT_BY_KEY, sortBy);
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem(QUICK_WINS_SORT_DIR_KEY, sortDirection);
    }, [sortDirection]);

    // Filter and sort groups by selected criteria
    const quickWinGroups = useMemo(() => {
        if (!animeGroups || animeGroups.length === 0) return [];

        // Calculate remaining time for each group, EXCLUDING dropped anime
        const groupsWithRemaining = animeGroups.map(group => {
            // Filter out dropped anime from the group
            const nonDroppedAnime = group.anime.filter(entry => entry.status !== 'DROPPED');

            // If all anime in this group are dropped, skip this group
            if (nonDroppedAnime.length === 0) {
                return { ...group, remainingTime: 0, hasNonDropped: false };
            }

            // Calculate remaining time only from non-dropped anime
            let watchedDuration = 0;
            let totalDuration = 0;

            nonDroppedAnime.forEach(entry => {
                const episodes = getTotalEpisodes(entry);
                const rawDuration = entry.media.duration || 24;
                const duration = rawDuration >= 10 ? Math.max(0, rawDuration - OP_ED_SKIP_MINUTES) : rawDuration;
                const watched = entry.progress || 0;

                totalDuration += episodes * duration;
                watchedDuration += watched * duration;
            });

            return {
                ...group,
                remainingTime: totalDuration - watchedDuration,
                hasNonDropped: true
            };
        });

        // Filter by quick win hours threshold AND exclude groups with only dropped anime
        const thresholdMinutes = quickWinHours * 60;
        let filtered = groupsWithRemaining
            .filter(group => group.hasNonDropped && group.remainingTime > 0 && group.remainingTime <= thresholdMinutes);

        // Apply sorting
        const dir = sortDirection === 'asc' ? 1 : -1;
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return dir * a.name.localeCompare(b.name);
                case 'averageScore':
                    return dir * (b.stats.averageScore - a.stats.averageScore);
                case 'totalDuration':
                    return dir * (b.stats.totalDuration - a.stats.totalDuration);
                case 'watchedTime':
                    return dir * (b.stats.watchedDuration - a.stats.watchedDuration);
                case 'totalAnime':
                    return dir * (b.stats.animeCount - a.stats.animeCount);
                case 'remainingTime':
                default:
                    return dir * (a.remainingTime - b.remainingTime);
            }
        });

        return filtered;
    }, [animeGroups, quickWinHours, titlePreference, sortBy, sortDirection]);

    // Calculate paginated groups to display
    const displayedGroups = useMemo(() => {
        const totalPages = Math.ceil(quickWinGroups.length / GROUPS_PER_PAGE);
        let groupsToShow = [];

        // Add groups from all loaded pages
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const startIndex = (page - 1) * GROUPS_PER_PAGE;
            const endIndex = startIndex + GROUPS_PER_PAGE;
            groupsToShow.push(...quickWinGroups.slice(startIndex, endIndex));
        });

        return groupsToShow;
    }, [quickWinGroups, loadedPages]);

    // Calculate total pages
    const totalPages = Math.ceil(quickWinGroups.length / GROUPS_PER_PAGE);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
        setLoadedPages(new Set([1]));
    }, [quickWinHours, sortBy, sortDirection]);

    // BUFFERED PAGINATION: Immediately load Page 2 if available
    useEffect(() => {
        if (currentPage === 1 && totalPages > 1 && !loadedPages.has(2)) {
            const timer = setTimeout(() => {
                setLoadedPages(prev => new Set([...prev, 2]));
                setCurrentPage(2);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Load next page
    const loadNextPage = React.useCallback(() => {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
            setLoadedPages(prev => new Set([...prev, nextPage]));
            setCurrentPage(nextPage);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Infinite scroll handler
    const handleScroll = React.useCallback(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight;
        const triggerHeight = pageHeight - 1000; // Load aggressively (1000px before bottom) to maintain buffer

        if (scrollPosition >= triggerHeight && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    // Attach scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleGroupClick = (groupId) => {
        const group = animeGroups.find(g => g.id === groupId);
        if (group) {
            setSelectedGroup(group);
        }
    };

    // Handle anime status change (same as Dashboard)
    const handleStatusChange = async (mediaId, currentStatus, newStatus, animeTitle) => {
        if (newStatus === currentStatus) return;

        const token = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        addChange({
            type: 'STATUS_CHANGE',
            mediaId,
            animeTitle,
            oldStatus: currentStatus,
            newStatus: newStatus,
            timestamp: Date.now()
        });

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            console.log(`✅ Updated ${animeTitle} to ${newStatus}`);
            if (refreshData) {
                refreshData(true);
            }
            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to update status for ${animeTitle}:`, error);
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');;
        }
    };

    // Handle score change
    const handleScoreChange = async (mediaId, newScoreRaw, animeTitle) => {
        const tkn = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }
        setOptimisticScores(prev => new Map(prev).set(mediaId, newScoreRaw));
        setUpdatingScores(prev => new Set([...prev, mediaId]));
        try {
            await updateMediaScore(mediaId, newScoreRaw, tkn);
            if (refreshData) refreshData(true);
        } catch (error) {
            showError(`Failed to update score for ${animeTitle}`, 'Score Update Failed');
            setOptimisticScores(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingScores(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle episode progress change
    const handleProgressChange = async (mediaId, newProgress, totalEpisodes, animeTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newProgress);
        if (totalEpisodes > 0) {
            clamped = Math.min(clamped, totalEpisodes);
        } else if (totalEpisodes === 0) {
            clamped = 0;
        }

        const token = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        setOptimisticProgress(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingProgress(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeProgress(mediaId, clamped, token);
            console.log(`✅ Updated ${animeTitle} progress to ${clamped}`);
            if (refreshData) {
                refreshData(true);
            }

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Watching)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${animeTitle} — progress set to ${clamped}, switching to Watching`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', animeTitle);
            }

            // Auto-complete: if all episodes watched and media is finished, mark as COMPLETED
            if (clamped > 0 && totalEpisodes > 0 && clamped === totalEpisodes && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${animeTitle} — all ${totalEpisodes} episodes watched`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', animeTitle);
            }
        } catch (error) {
            console.error(`❌ Failed to update progress for ${animeTitle}:`, error);
            showError(`Failed to update progress for ${animeTitle}`, 'Update Failed');
            setOptimisticProgress(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
        } finally {
            setUpdatingProgress(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        }
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedGroup) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedGroup]);

    // Show loading when app is loading (initial or refresh) and no data cached yet
    if (isLoading && (!animeGroups || animeGroups.length === 0)) {
        return <CardGridSkeleton title="⚡ Quick Wins" subtitle="Anime groups you can complete soon" />;
    }

    if (!animeGroups || animeGroups.length === 0) {
        return (
            <div className="quick-wins-container">
                <div className="empty-state">
                    <div className="empty-icon">⚡</div>
                    <h2>No Groups Yet</h2>
                    <p>Your anime groups will appear here once loaded.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="quick-wins-container">
            <div className="quick-wins-header">
                <div className="header-left">
                    <h1>⚡ Quick Wins</h1>
                    <p>Anime groups you can complete soon</p>
                </div>
                <div className="quick-wins-filter">
                    <label htmlFor="quick-win-hours">Complete in</label>
                    <input
                        id="quick-win-hours"
                        type="number"
                        min="1"
                        max="999"
                        value={quickWinHours}
                        onChange={(e) => setQuickWinHours(e.target.value)}
                        className="hours-input"
                    />
                    <span className="hours-label">hours</span>
                    <div className="sort-controls">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                            title="Sort by"
                        >
                            {SORT_OPTIONS.map(option => (
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                            className="sort-direction-btn"
                            title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                        >
                            {sortDirection === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="quick-wins-count">
                {quickWinGroups.length > 0
                    ? `${quickWinGroups.length} groups can be completed within ${quickWinHours} hour${quickWinHours !== 1 ? 's' : ''}`
                    : `No groups can be completed within ${quickWinHours} hour${quickWinHours !== 1 ? 's' : ''}`
                }
            </div>

            <div className="quick-wins-list">
                {quickWinGroups.length === 0 ? (
                    <div className="empty-state">
                        <p>No quick wins found. Try increasing the hour threshold.</p>
                    </div>
                ) : (
                    displayedGroups.map((group, index) => {
                        // Get the oldest anime for cover image
                        const oldestAnime = group.anime[0];
                        const coverImage = oldestAnime?.media?.coverImage?.extraLarge || oldestAnime?.media?.coverImage?.large || oldestAnime?.media?.coverImage?.medium;
                        const groupName = getDisplayTitle(oldestAnime?.media, titlePreference);
                        const hasReleasing = group.anime.some(a => a.media?.status === 'RELEASING');

                        return (
                            <div
                                key={group.id}
                                className="quick-win-card"
                                onClick={() => handleGroupClick(group.id)}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleGroupClick(group.id);
                                    }
                                }}
                            >
                                <div className="quick-win-rank">#{index + 1}</div>

                                {coverImage && (
                                    <img
                                        src={coverImage}
                                        alt={groupName}
                                        className="quick-win-cover"
                                    />
                                )}

                                <div className="quick-win-info">
                                    <h3 className="quick-win-name">
                                        <a
                                            href={`https://anilist.co/anime/${oldestAnime?.media?.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {groupName}
                                        </a>
                                    </h3>
                                    {hasReleasing && <span className="group-releasing-tag">📡 Airing</span>}

                                    <div className="quick-win-stats">
                                        <span className="stat-anime-count">
                                            📚 {group.stats.animeCount} anime
                                        </span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            🎬 {formatEpisodeProgress(group.stats.watchedEpisodes, group.stats.totalEpisodes)} eps
                                        </span>
                                        {group.stats.averageScore > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">
                                                    ⭐ {(group.stats.averageScore / 10).toFixed(1)}
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    <div className="quick-win-time-stats">
                                        <div className="time-stat remaining primary">
                                            <span className="time-label">⏳ To Complete</span>
                                            <span className="time-value">
                                                {formatDuration(group.remainingTime, hoursPerDay)}
                                            </span>
                                        </div>
                                        {group.stats.watchedDuration > 0 && (
                                            <div className="time-stat watched">
                                                <span className="time-label">✅ Watched</span>
                                                <span className="time-value">
                                                    {formatDuration(group.stats.watchedDuration, hoursPerDay)}
                                                </span>
                                            </div>
                                        )}
                                        <div className="time-stat total">
                                            <span className="time-label">📺 Total</span>
                                            <span className="time-value">
                                                {formatDuration(group.stats.totalDuration, hoursPerDay)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Group Card Modal Overlay */}
            {selectedGroup && (
                <div className="group-modal-overlay" onClick={() => setSelectedGroup(null)}>
                    <div className="group-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setSelectedGroup(null)}>×</button>

                        <div className="group-card-modal">
                            <div className="modal-group-header">
                                {selectedGroup.anime[0]?.media?.coverImage?.large && (
                                    <img
                                        src={selectedGroup.anime[0].media.coverImage.large}
                                        alt={getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                        className="modal-group-cover"
                                    />
                                )}
                                <div className="modal-group-info">
                                    <h2 className="modal-group-name">
                                        📚 {getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                    </h2>
                                    <div className="modal-group-stats">
                                        <span className="stat-anime-count">{selectedGroup.stats.animeCount} anime</span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            {formatEpisodeProgress(selectedGroup.stats.watchedEpisodes, selectedGroup.stats.totalEpisodes)} eps
                                        </span>
                                        {selectedGroup.stats.averageScore > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">⭐ {(selectedGroup.stats.averageScore / 10).toFixed(1)}</span>
                                            </>
                                        )}
                                        <span>•</span>
                                        <span className="stat-total-time">📺 {formatDuration(selectedGroup.stats.totalDuration, hoursPerDay)}</span>
                                        {selectedGroup.stats.watchedDuration > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-watched-time">✅ {formatDuration(selectedGroup.stats.watchedDuration, hoursPerDay)}</span>
                                            </>
                                        )}
                                        {(selectedGroup.stats.totalDuration - selectedGroup.stats.watchedDuration) > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-remaining-time">⏳ {formatDuration(selectedGroup.stats.totalDuration - selectedGroup.stats.watchedDuration, hoursPerDay)}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-anime-list">
                                {selectedGroup.anime.map((entry, index) => {
                                    const title = getDisplayTitle(entry.media, titlePreference);
                                    const releaseDate = formatReleaseDate(entry.media.startDate);
                                    const totalEpisodes = getTotalEpisodes(entry);
                                    const watchedEpisodes = entry.progress || 0;
                                    const animeTime = calculateAnimeTime(entry);
                                    const displayStatus = optimisticUpdates.get(entry.media.id) || entry.status;
                                    const watchedEpisodesDisplay = optimisticProgress.has(entry.media.id) ? optimisticProgress.get(entry.media.id) : watchedEpisodes;

                                    return (
                                        <div key={entry.id} className="modal-anime-item">

                                            <img
                                                src={entry.media.coverImage?.extraLarge || entry.media.coverImage?.large || entry.media.coverImage?.medium}
                                                alt={title}
                                                className="anime-cover"
                                            />
                                            <div className="anime-details">
                                                <div className="anime-title-row">
                                                    <h4 className="anime-title">
                                                        <a
                                                            href={`https://anilist.co/anime/${entry.media.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {title}
                                                        </a>
                                                    </h4>
                                                    <div className="anime-action-buttons">
                                                        <ScoreInput
                                                            mediaId={entry.media.id}
                                                            currentScore={optimisticScores.has(entry.media.id) ? optimisticScores.get(entry.media.id) : (entry.score || 0)}
                                                            isUpdating={updatingScores.has(entry.media.id)}
                                                            onChange={(newScore) => handleScoreChange(entry.media.id, newScore, title)}
                                                        />
                                                        <EpisodeCounter
                                                            mediaId={entry.media.id}
                                                            currentProgress={watchedEpisodesDisplay}
                                                            totalEpisodes={totalEpisodes}
                                                            isUpdating={updatingProgress.has(entry.media.id)}
                                                            onChange={(newVal) => handleProgressChange(entry.media.id, newVal, totalEpisodes, title, entry.media.status, displayStatus)}
                                                        />
                                                        <StatusButton
                                                            currentStatus={displayStatus}
                                                            onChange={(newStatus) => handleStatusChange(entry.media.id, displayStatus, newStatus, title)}
                                                            disabled={changingStatus.has(entry.media.id)}
                                                            isLoading={changingStatus.has(entry.media.id)}
                                                        />
                                                        <button
                                                            className="action-btn watch-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedAnimeForModal(title);
                                                                setShowWatchModal(true);
                                                            }}
                                                            title="Watch"
                                                        >
                                                            🎬
                                                        </button>

                                                    </div>
                                                </div>
                                                <div className="anime-meta">
                                                    <span>{releaseDate}</span>
                                                    <span>{entry.media.format}</span>
                                                    <span>{watchedEpisodes}/{totalEpisodes} eps</span>
                                                    {entry.score > 0 && <span>⭐ {(entry.score / 10).toFixed(1)}</span>}
                                                    {entry.media.averageScore > 0 && <span>📊 {(entry.media.averageScore / 10).toFixed(1)}</span>}
                                                    <span>📺 {formatDuration(animeTime.total, hoursPerDay)}</span>
                                                    {animeTime.watched > 0 && (
                                                        <span>✅ {formatDuration(animeTime.watched, hoursPerDay)}</span>
                                                    )}
                                                    {animeTime.remaining > 0 && (
                                                        <span>⏳ {formatDuration(animeTime.remaining, hoursPerDay)}</span>
                                                    )}
                                                    {(() => { const badge = getMediaStatusBadge(entry.media?.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Site Links Modals */}
            <SiteLinksModal
                isOpen={showWatchModal}
                onClose={() => {
                    setShowWatchModal(false);
                    setSelectedAnimeForModal('');
                }}
                links={watchLinks}
                animeName={selectedAnimeForModal || ''}
                type="watch"
            />

        </div>
    );
};

export default QuickWins;
